import React, { useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Step1AccountInfo from './registration/Step1AccountInfo';
import Step2PersonalInfo from './registration/Step2PersonalInfo';
import Step3Introduction from './registration/Step3Introduction';
import Step4PrayerUnit from './registration/Step4PrayerUnit';
import Step5AdditionalInfo from './registration/Step5AdditionalInfo';
import Step6Terms from './registration/Step6Terms';
import Footer from '../Footer';
import { useLanguage } from '../../contexts/LanguageContext';

// Registration Context
const RegistrationContext = createContext();

export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

const Registration = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Account Information
    email: '',
    password: '',
    confirmPassword: '',
    
    // Personal Information
    firstName: '',
    lastName: '',
    gender: '',
    phoneNumber: '',
    
    // Prayer Unit
    prayerUnit: '',
    prayerUnitName: '',
    
    // Additional Information
    day: '',
    month: '',
    year: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    
    // Terms
    privacyAgreement: false
  });

  const totalSteps = 6;

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1AccountInfo />;
      case 2:
        return <Step2PersonalInfo />;
      case 3:
        return <Step3Introduction />;
      case 4:
        return <Step4PrayerUnit />;
      case 5:
        return <Step5AdditionalInfo />;
      case 6:
        return <Step6Terms />;
      default:
        return <Step1AccountInfo />;
    }
  };

  const contextValue = {
    currentStep,
    totalSteps,
    formData,
    updateFormData,
    nextStep,
    prevStep,
    navigate
  };

  const registrationCopy = t('registration', {});
  const progressLabel = (registrationCopy.stepProgress || 'Step {current} of {total}')
    .replace('{current}', currentStep)
    .replace('{total}', totalSteps);

  return (
    <RegistrationContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-br from-prayer-red via-purple-600 to-blue-600 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              {registrationCopy.title}
            </h1>
            <div className="flex justify-center items-center space-x-2">
              <span className="text-sm font-medium text-white/80">
                {progressLabel}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4 w-full bg-white/20 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
            {renderStep()}
          </div>

          {/* Navigation Buttons - Step 6 handles its own submit via Step6Terms form */}
          {currentStep < totalSteps && (
            <div className="flex justify-between mt-6">
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 text-white/80 hover:text-white font-medium transition-colors duration-200"
                >
                  {registrationCopy.backToHome}
                </button>
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    currentStep === 1
                      ? 'bg-white/20 text-white/50 cursor-not-allowed'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {registrationCopy.previous || t('common.previous')}
                </button>
              </div>
              <button
                onClick={nextStep}
                className="px-6 py-3 bg-white text-prayer-red rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 shadow-lg"
              >
                {registrationCopy.next || t('common.next')}
              </button>
            </div>
          )}

          {/* Back to Login */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/auth/login')}
              className="text-white/80 hover:text-white font-medium"
            >
              {registrationCopy.alreadyHaveAccount}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </RegistrationContext.Provider>
  );
};

export default Registration;
